import { useCallback } from 'react'
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { resizeImage } from '@wehunt/ui'

/**
 * Returns an async uploader that persists an image inside the Plan-hunt
 * wizard's Delta editor to Firebase Storage at
 * `huntPlans/{draftId}/images/{uuid}.{ext}`, returning a public download URL
 * for embedding in the Delta document.
 *
 * The uploader is bound to a specific `draftId`. Callers must guarantee a
 * non-null draftId before invoking — in the wizard this is true by the time
 * the user is on Step 4 (Invitation), since every Continue persists the
 * draft. When draftId is null we throw so the editor's image button
 * surfaces a console error rather than silently dropping the upload.
 *
 * Images are resized client-side to a max 1200 px longest dimension before
 * upload so the storage bucket doesn't accumulate raw phone photos at
 * 10+ MB each. Already-small images pass through unchanged.
 */
export function useUploadHuntImage(draftId: string | null) {
  return useCallback(
    async (file: File): Promise<string> => {
      if (!draftId) {
        throw new Error(
          'useUploadHuntImage: draftId is null. Save the draft before uploading images.',
        )
      }

      const blob = await resizeImage(file)
      const extension = pickExtension(file.name, blob.type)
      const objectName = `${cryptoRandomId()}.${extension}`
      const path = `huntPlans/${draftId}/images/${objectName}`

      const storage = getStorage()
      const objectRef = ref(storage, path)
      await uploadBytes(objectRef, blob, { contentType: blob.type || file.type })
      return await getDownloadURL(objectRef)
    },
    [draftId],
  )
}

function pickExtension(fileName: string, mimeType: string): string {
  const dot = fileName.lastIndexOf('.')
  if (dot > 0 && dot < fileName.length - 1) {
    return fileName.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '')
  }
  // Fallback to MIME-derived extension when the source file name lacks one
  // (mobile photo pickers sometimes hand us `image/jpeg` blobs with no name).
  if (mimeType === 'image/jpeg') return 'jpg'
  if (mimeType === 'image/png') return 'png'
  if (mimeType === 'image/webp') return 'webp'
  return 'bin'
}

function cryptoRandomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '')
  }
  // Last-resort fallback for ancient runtimes; the editor only runs in
  // modern browsers, so this branch is effectively dead but kept for safety.
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}
